import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Switch, Space, ConfigProvider, theme, Select, Button as AntdButton } from "antd";
import { Input } from "@/components/ui/inputForm";
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getAllEspecificas, getEspecificaById, saveEspecifica } from "@/services/especifica";
import { message } from "antd";
import { usePermissions } from "@/hooks/usePermissions";

export default function ConsultaEspecifica() {
  const [especificas, setEspecificas] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    getAllEspecificas().then((res) => {
      if (res.status === 200) {
        setEspecificas(res.data.map((s: any) => ({ ...s, ativo: s.ativo ?? true })));
      }
    });
  }, []);

  const handleAdd = useCallback(() => {
    navigate("/especifica/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/especifica/${id}`);
  };

  const toggleAtivo = async (id: string) => {
    try {
      const { data: especifica } = await getEspecificaById(id);
      const updatedEspecifica = { ...especifica, ativo: !especifica.ativo };
      const response = await saveEspecifica(updatedEspecifica);
      if (response.status === 200 || response.status === 201) {
        setEspecificas((prev) =>
          prev.map((s) => (s.id === id ? { ...s, ativo: !s.ativo } : s))
        );
        message.success(`Específica ${!especifica.ativo ? 'ativada' : 'inativada'} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status da específica.");
      }
    } catch (error) {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const filteredEspecificas = especificas.filter((s) => {
    const matchesSearch = s.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && s.ativo) ||
      (statusFilter === "inativos" && !s.ativo);
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (value: number) => value != null ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "-",
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch checked={!!value} onChange={() => toggleAtivo(record.id)} checkedChildren="Ativo" unCheckedChildren="Inativo" />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {canEdit(12) && (
            <AntdButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Específicas</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativos", label: "Ativos" },
              { value: "inativos", label: "Inativos" },
            ]}
          />
          {canInsert(12) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredEspecificas} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
} 